import { FC, useEffect, useRef, useState } from 'react';
import { Button, Cascader, Col, Form, Row, Space } from 'antd';
import cs from 'classnames';
import dayjs from 'dayjs';
import { getCookie } from '@lhb/cache';
import { isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { useSelector } from 'react-redux';
import V2Container from '@/common/components/Data/V2Container';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormRangeInput from '@/common/components/Form/V2FormRangeInput/V2FormRangeInput';
import V2FormRangePicker from '@/common/components/Form/V2FormRangePicker/V2FormRangePicker';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import CirclebusinessMap from '@/common/components/business/ExpandStore/CircleBusinessMap';
import { AssignType } from '@/common/components/business/ExpandStore/ts-config';
import { createLocxxDirectTask } from '@/common/api/expandStore/expansiontask';
import { ResponsibleAccountType } from '@/views/expandstore/pages/expansiontask/ts-config';
import AssignResponsibleAccount from '@/views/expandstore/pages/expansioncirclesenmatask/components/Edit/AssignResponsibleAccount';
import {
  buildLocxxDirectTaskCreateParams,
  buildLocxxMapBusinessInitialValues,
  buildLocxxTaskName,
  LocxxDirectTaskCreateValues,
  validateLocxxTaskCreateValues,
} from '../adapters';
import styles from './LocxxTaskCreateDrawer.module.less';

const marketingPurposeOptions = [
  { label: '品牌宣传', value: 6 },
  { label: '获客', value: 7 },
  { label: '售卖', value: 8 },
];

const activityPeriodUnitOptions = [
  { label: '日', value: 1 },
  { label: '周', value: 2 },
  { label: '月', value: 3 },
];

const dateFormItemConfig = {
  getValueFromEvent: (...args: any[]) => args[1],
  getValueProps: (value: string) => ({ value: value ? dayjs(value, 'YYYY-MM-DD') : undefined }),
};

const rangeFormItemConfig = {
  getValueFromEvent: (...args: any[]) => {
    const dateString = args[1];
    return Array.isArray(dateString) && dateString.filter((item) => isNotEmpty(item)).length ? dateString : [];
  },
  getValueProps: (value: string[]) => ({
    value: Array.isArray(value) && value.filter((item) => isNotEmpty(item)).length === 2 ? [dayjs(value[0], 'YYYY-MM-DD'), dayjs(value[1], 'YYYY-MM-DD')] : undefined,
  }),
};

interface LocxxTaskCreateDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessInfo?: any;
  isBusiness?: boolean;
}

const LocxxTaskCreateDrawer: FC<LocxxTaskCreateDrawerProps> = ({ open, onClose, onSuccess, businessInfo, isBusiness = false }) => {
  const [form] = Form.useForm();
  const [innerMainHeight, setInnerMainHeight] = useState<number>(0);
  const [requesting, setRequesting] = useState(false);
  const circlebusinessMap = useRef<any>();
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const targetAddressInfo = Form.useWatch('targetAddressInfo', form);
  const businessName = Form.useWatch('businessName', form);
  const isAssignTypeLeader = [AssignType.Twice, AssignType.ThreeTimes].includes(tenantCheckData?.taskAssignType);

  const methods = useMethods({
    resetForm() {
      form.resetFields();
      setRequesting(false);
    },

    initializeBusiness() {
      if (!isBusiness || !businessInfo) {
        return;
      }
      form.setFieldsValue(buildLocxxMapBusinessInitialValues(businessInfo));
    },

    handleClose() {
      methods.resetForm();
      onClose();
    },

    syncTaskName() {
      const cityName = Array.isArray(targetAddressInfo) ? targetAddressInfo[1] : '';
      form.setFieldValue('name', buildLocxxTaskName(cityName, businessName));
    },

    async handleSubmit() {
      const values = await form.validateFields().catch(() => null);
      if (!values) {
        return;
      }
      const validationResult = validateLocxxTaskCreateValues(values);
      if (!validationResult.valid) {
        V2Message.warning(validationResult.message || '请检查表单填写');
        return;
      }
      const radio = values.radio;
      if (radio === ResponsibleAccountType.ASSIGN_SELF) {
        values.assignAccountIds = [getCookie('employeeId')];
      } else if (radio === ResponsibleAccountType.ASSIGN_OTHER && isAssignTypeLeader) {
        if (!values.managerId) {
          V2Message.warning('请选择开发主管');
          return;
        }
      } else if (radio === ResponsibleAccountType.ASSIGN_OTHER && values.assignAccountIds) {
        values.assignAccountIds = [values.assignAccountIds];
      } else {
        V2Message.warning('请选择指派的成员');
        return;
      }
      const cityName = Array.isArray(values.targetAddressInfo) ? values.targetAddressInfo[1] : '';
      const params = buildLocxxDirectTaskCreateParams(values as LocxxDirectTaskCreateValues, cityName);
      try {
        setRequesting(true);
        await createLocxxDirectTask(params);
        V2Message.success('创建拓店任务成功');
        methods.handleClose();
        onSuccess();
      } finally {
        setRequesting(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      methods.initializeBusiness();
      return;
    }
    methods.resetForm();
  }, [open, businessInfo, isBusiness]);

  useEffect(() => {
    methods.syncTaskName();
  }, [targetAddressInfo, businessName]);

  return (
    <V2Drawer className={styles.drawer} open={open} onClose={methods.handleClose} destroyOnClose bodyStyle={{ padding: 0 }}>
      <V2Container
        emitMainHeight={(height) => setInnerMainHeight(height)}
        className={styles.container}
        style={{ height: '100%', width: '100%' }}
        extraContent={{
          top: <V2Title type='H1' text='创建拓店任务' className='pl-40 pt-16' />,
          bottom: <Space className={styles.bottom} size={12}>
            <Button onClick={methods.handleClose}>取消</Button>
            <Button type='primary' loading={requesting} onClick={methods.handleSubmit}>确定</Button>
          </Space>,
        }}
      >
        <V2Form
          form={form}
          className={cs('pt-16', 'pb-16', 'pl-40', 'pr-40', styles.form)}
          style={{
            height: innerMainHeight,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <V2Title type='H2' divider text='拓店任务信息' className='mb-16' />
          <Row gutter={24}>
            <Col span={24} className='mb-16'>
              <CirclebusinessMap form={form} ref={circlebusinessMap} businessInfo={businessInfo} isBusiness={isBusiness} />
            </Col>
            <Form.Item name='circleBusinessId' hidden noStyle />
            <Col span={12}>
              <V2FormInput required label='商圈' name='businessName' disabled placeholder='请在地图上选择商圈' />
            </Col>
            <Col span={12}>
              <Form.Item name='targetAddressInfo' hidden noStyle />
              <V2FormProvinceList
                required
                label='目标城市'
                name='targetAddress'
                disabled
                config={{
                  value: [1],
                  changeOnSelect: true,
                  allowClear: true,
                  expandTrigger: 'hover',
                  showCheckedStrategy: Cascader.SHOW_PARENT,
                }}
              />
            </Col>
            <Col span={12}>
              <V2FormInput required label='拓店任务名称' name='name' disabled />
            </Col>
            <Col span={12}>
              <V2FormSelect label='营销目的' name='marketingPurpose' options={marketingPurposeOptions} />
            </Col>
            <Col span={12}>
              <V2FormRangeInput label='面积范围' name={['areaRequirementMin', 'areaRequirementMax']} min={0} precision={1} minPlaceholder='最小面积' maxPlaceholder='最大面积' extra='㎡' />
            </Col>
            <Col span={12}>
              <V2FormInputNumber label='层高要求' name='floorHeight' min={0} precision={1} config={{ addonAfter: 'm' }} />
            </Col>
            <Col span={12}>
              <V2FormInputNumber label='客流底线' name='minFlowBottomLine' min={0} precision={0} config={{ addonAfter: '人/天' }} />
            </Col>
            <Col span={12}>
              <V2FormInputNumber label='活动周期' name='activityPeriodCount' min={1} precision={0} config={{ addonAfter: <V2FormSelect name='activityPeriodUnit' noStyle allowClear options={activityPeriodUnitOptions} /> }} />
            </Col>
            <Col span={12}>
              <V2FormRangePicker label='活动日期' name='promotionDate' formItemConfig={rangeFormItemConfig} />
            </Col>
            <Col span={12}>
              <V2FormInputNumber required label='总预算' name='totalBudget' min={0} precision={2} config={{ addonAfter: '万元' }} />
            </Col>
            <Col span={12}>
              <V2FormDatePicker required label='期望落位日期' name='expectDropInDate' formItemConfig={dateFormItemConfig} />
            </Col>
          </Row>
          <V2Title type='H2' divider text='选择拓店责任人' className='mt-16 mb-16' />
          <AssignResponsibleAccount form={form} />
        </V2Form>
      </V2Container>
    </V2Drawer>
  );
};

export default LocxxTaskCreateDrawer;
